/**
 * Your shadows (lane ALGOBOO2, SHADOW1): each agent you follow with play money, and for the one chosen what you would
 * have had -- simulated by the platform's paper fill model at your size and delay, gas counted, open positions at what
 * they cost (never marked). Beside it, what a real follower would pay: the agent's fee applied to the result, and once
 * followed what the real permission was charged (lane BOOUX1, A2-5). "Follow for real" turns it into a real
 * permission, signed with your own wallet, whenever you choose -- no waiting period (A2-6). What the simulation does
 * not do (the price move while a copy waits out your delay, A2-4) is said by name.
 */
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import type { ShadowView } from '@algoboo/schema/api'
import { Button, Card, EmptyState, Pill, Skeleton, Stat, UiLink, buttonClasses, cx, formatCount, formatEth, fullTime, paths, shortAddress } from '@algoboo/ui'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { myShadowsQuery, request, useMyShadow, useMyShadows } from '../lib/api'
import { productKeys } from '../lib/api/hooks/product'
import { usePageMeta } from '../lib/meta'
import { PageHeader } from '../components/Shell'
import { SignInPrompt, useSession } from '../components/session'
import { LimitNote } from '../components/LimitNote'
import { AgentAvatar, QueryError } from '../components/widgets'
import { prefetchSignedIn } from '../lib/route-data'

export const Route = createFileRoute('/portfolio/shadows')({
  validateSearch: z.object({ ref: z.string().optional().catch(undefined) }),
  loader: ({ context: { queryClient: qc } }) => prefetchSignedIn(qc, () => [qc.ensureQueryData(myShadowsQuery())]),
  component: ShadowsPage,
})

const STATE_WORDS: Record<ShadowView['state'], string> = { active: 'shadowing', stopped: 'stopped', upgraded: 'followed for real' }

function Simulated() {
  return (
    <Pill tone="warn" title="Simulated with the platform's fill model: nothing was bought">
      simulated
    </Pill>
  )
}

function ShadowsPage() {
  usePageMeta('Shadows')
  const { me, loading } = useSession()
  const { ref } = Route.useSearch()
  if (loading) return <Skeleton className="h-64 rounded-xl" />
  if (!me) return <SignInPrompt title="Your shadows" body="Follow agents with play money and see what you would have made. Sign in with your wallet to see yours." />
  return <Shadows selected={ref} />
}

function Shadows({ selected }: { selected: string | undefined }) {
  const list = useMyShadows()
  const navigate = useNavigate({ from: '/portfolio/shadows' })
  const rows = list.data ?? []
  const ref = selected ?? rows[0]?.shadowRef
  return (
    <div className="space-y-5">
      <PageHeader title="Shadows" lede="Agents you follow with play money. Every figure here is simulated." />
      {list.isLoading ? (
        <Skeleton className="h-24 rounded-xl" />
      ) : list.error ? (
        <QueryError error={list.error} refetch={() => void list.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState title="No shadows yet" body="Open an agent's page and press Shadow to follow it with play money." />
      ) : (
        <div className="grid gap-5 lg:grid-cols-[300px_minmax(0,1fr)]">
          <ul className="space-y-2" aria-label="Your shadows">
            {rows.map((s) => (
              <li key={s.shadowRef}>
                <button
                  type="button"
                  onClick={() => void navigate({ search: { ref: s.shadowRef }, replace: true })}
                  className={cx('flex w-full items-center gap-3 rounded-lg border p-3 text-left', s.shadowRef === ref ? 'border-brand bg-brand-soft' : 'border-line hover:border-line-strong')}
                  data-testid="shadow-row"
                  data-state={s.state}
                >
                  {s.agent && <AgentAvatar handle={s.agent.handle} name={s.agent.name} avatar={s.agent.avatar} avatarUrl={s.agent.avatarUrl} size={32} />}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14px] font-medium">{s.agent?.name ?? 'An agent'}</span>
                    <span className="block text-[12px] text-ink-3">
                      {formatEth(s.lotEth)} a trade · {STATE_WORDS[s.state]}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {ref && <ShadowDetail shadowRef={ref} />}
        </div>
      )}
    </div>
  )
}

function ShadowDetail({ shadowRef }: { shadowRef: string }) {
  const q = useMyShadow(shadowRef)
  const qc = useQueryClient()
  const [busy, setBusy] = useState(false)
  if (q.isLoading) return <Skeleton className="h-80 rounded-xl" />
  if (q.error || !q.data) return <QueryError error={q.error} refetch={() => void q.refetch()} />
  const s = q.data
  const r = s.result
  const stop = () => {
    setBusy(true)
    return request('stopMyShadow', { params: { ref: s.shadowRef } })
      .then(() => qc.invalidateQueries({ queryKey: productKeys.shadows() }))
      .finally(() => setBusy(false))
  }
  return (
    <Card className="space-y-5 p-5" data-testid="shadow-detail" data-state={s.state}>
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-display text-[18px] font-semibold">
          {s.agent ? (
            <UiLink href={paths.agent(s.agent.handle)} className="hover:underline">
              {s.agent.name}
            </UiLink>
          ) : (
            'An agent'
          )}
        </h2>
        <Simulated />
        <span className="text-[12.5px] text-ink-3">
          {formatEth(s.budgetEth)} play money · {formatEth(s.lotEth)} a trade · {formatCount(s.delaySeconds)} s behind · since {fullTime(s.startedAt)}
        </span>
      </div>
      {!r ? (
        <p className="text-[13px] text-ink-3">The result could not be simulated just now.</p>
      ) : (
        <>
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="shadow-result">
            <Stat label="You would have, at cost">{formatEth(r.heldAtCostEth)}</Stat>
            <Stat label="Net result">{r.netEth === null ? 'not known yet' : formatEth(r.netEth, { sign: 'always' })}</Stat>
            <Stat label="Gas paid">{formatEth(r.gasEth)}</Stat>
            <Stat label="Copies">
              {formatCount(r.filled)} of {formatCount(r.copies)} filled
            </Stat>
          </dl>
          <div className="rounded-lg border border-line bg-surface-2 p-3" data-testid="shadow-fees">
            <h3 className="text-[13px] font-semibold">What a real follower would pay</h3>
            <dl className="mt-1 grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Stat label="Performance fee">{s.fees.performanceFeeBps === 0 ? 'none' : `${s.fees.performanceFeeBps / 100}%`}</Stat>
              <Stat label="Fee on this result">{s.fees.feeEth === null ? 'not known yet' : formatEth(s.fees.feeEth)}</Stat>
              <Stat label="Net after the fee">{s.fees.netAfterFeeEth === null ? 'not known yet' : formatEth(s.fees.netAfterFeeEth, { sign: 'always' })}</Stat>
            </dl>
            <p className="mt-1 text-[12px] text-ink-3">{s.fees.words}</p>
            {s.fees.assessedEth !== null && <p className="mt-1 text-[12px] text-ink-2">Charged on your real permission so far: {formatEth(s.fees.assessedEth)}.</p>}
          </div>
          <p className="text-[12px] text-ink-3" data-testid="shadow-costs">
            Cash {formatEth(r.cashEth)}; spent {formatEth(r.spentEth)}, got back {formatEth(r.proceedsEth)}, realized {formatEth(r.realizedEth, { sign: 'always' })}. Open positions count at what they cost; they are not priced.
            {!r.complete && ' The agent traded more than one reading covers: this counts its oldest trades.'}
          </p>
          {r.openLots.length > 0 && (
            <div>
              <h3 className="text-[13px] font-semibold">Still held</h3>
              <ul className="mt-1 space-y-1 text-[12.5px]">
                {r.openLots.map((l) => (
                  <li key={l.token} className="flex justify-between">
                    <UiLink href={paths.token(l.token)} className="font-mono text-accent hover:underline">
                      {shortAddress(l.token)}
                    </UiLink>
                    <span className="text-ink-2">cost {formatEth(l.costEth)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {[...r.refused, ...r.skipped].length > 0 && (
            <div data-testid="shadow-reasons">
              <h3 className="text-[13px] font-semibold">Not copied</h3>
              <ul className="mt-1 space-y-1 text-[12.5px] text-ink-2">
                {[...r.refused, ...r.skipped].map((c) => (
                  <li key={c.reason}>
                    {formatCount(c.count)} × {c.words}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div>
            <h3 className="text-[13px] font-semibold">Latest copies</h3>
            <ul className="mt-1 divide-y divide-line text-[12.5px]">
              {r.recent.map((c, i) => (
                <li key={`${c.decidedAt ?? ''}:${i}`} className="flex items-center justify-between gap-2 py-1.5" data-testid="shadow-copy">
                  <span>
                    {c.side === 'buy' ? 'Buy' : 'Sell'} <span className="font-mono">{shortAddress(c.token)}</span> · {c.words}
                  </span>
                  <span className="shrink-0 text-ink-3">{c.decidedAt ? fullTime(c.decidedAt) : ''}</span>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
      {s.modelWords && <p className="text-[12px] text-ink-3">{s.modelWords}</p>}
      <LimitNote limit={s.delayMove} />
      {s.notModelled.length > 0 && (
        <ul className="list-disc pl-5 text-[12px] text-ink-3" aria-label="What the simulation leaves out">
          {s.notModelled.map((n) => (
            <li key={n}>{n}</li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-2 border-t border-line pt-4">
        {s.upgrade.available && s.agent && (
          <Link to="/portfolio" search={{ grant: s.agent.id, shadow: s.shadowRef }} className={buttonClasses('primary', 'md')} data-testid="shadow-upgrade">
            Follow for real
          </Link>
        )}
        {s.state === 'active' && (
          <Button variant="ghost" onClick={() => void stop()} loading={busy} data-testid="shadow-stop">
            Stop shadowing
          </Button>
        )}
        <p className="basis-full text-[12px] text-ink-3">{s.upgrade.words}</p>
        {s.state === 'active' && (
          <p className="basis-full text-[12px] text-ink-3" data-testid="shadow-upgrade-rule">
            {s.upgrade.rule}
          </p>
        )}
      </div>
    </Card>
  )
}
